import { memo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';

import { localAudioToggled, localVideoToggled } from 'features/streaming/actions';
import { selectDeviceInfo } from 'features/application/applicationSlice';
import { useChat } from 'features/chat/useChat';
import { useSidePanel } from 'features/side-panels/hooks/useSidePanel';
import { selectLocalPublisherJoined } from 'features/streaming/streamingSlice';

// Sets up keyboard shortcuts for:
// - Audio toggle: Shift + Cmd + A (macOS) / Shift + Alt + A (Windows)
// - Video toggle: Shift + Cmd + V (macOS) / Shift + Alt + V (Windows)
// - Chat toggle: Cmd + Shift + H (macOS) / Alt + H (Windows)
const RoomKeyboardShortcuts = memo(() => {
  const dispatch = useAppDispatch();
  const { hasPermissions } = usePermissions();

  const allowVideoBroadcast = hasPermissions(PermissionTypes.broadcastVideo);
  const allowAudioBroadcast = hasPermissions(PermissionTypes.broadcastAudio);

  const isConnectedToFeed = useAppSelector(selectLocalPublisherJoined);

  const audioBroadcastAvailable = allowAudioBroadcast && isConnectedToFeed;
  const videoBroadcastAvailable = allowVideoBroadcast && isConnectedToFeed;

  const { chatAvailable } = useChat();
  const { togglePanel } = useSidePanel();

  const deviceInfo = useAppSelector(selectDeviceInfo);
  const isMacOS = deviceInfo.os.name === 'Mac OS';

  useEffect(() => {
    if (!audioBroadcastAvailable) {
      return;
    }

    const handleAudioKeydown = (event: KeyboardEvent) => {
      if (!event.key) {
        return;
      }

      const key = event.key.toLowerCase();

      const isMacCombination = isMacOS && event.metaKey && event.shiftKey;
      const isWindowsCombination = !isMacOS && event.altKey && event.shiftKey;

      if ((isMacCombination || isWindowsCombination) && key === 'a') {
        dispatch(localAudioToggled());
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleAudioKeydown);

    return () => {
      window.removeEventListener('keydown', handleAudioKeydown);
    };
  }, [dispatch, audioBroadcastAvailable, isMacOS]);

  useEffect(() => {
    if (!videoBroadcastAvailable) {
      return;
    }

    const handleVideoKeydown = (event: KeyboardEvent) => {
      if (!event.key) {
        return;
      }

      const key = event.key.toLowerCase();

      const isMacCombination = isMacOS && event.metaKey && event.shiftKey;
      const isWindowsCombination = !isMacOS && event.altKey && event.shiftKey;

      if ((isMacCombination || isWindowsCombination) && key === 'v') {
        dispatch(localVideoToggled());
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleVideoKeydown);

    return () => {
      window.removeEventListener('keydown', handleVideoKeydown);
    };
  }, [dispatch, videoBroadcastAvailable, isMacOS]);

  useEffect(() => {
    if (!chatAvailable) {
      return;
    }

    const handleChatKeydown = (event: KeyboardEvent) => {
      if (!event.key) {
        return;
      }

      const key = event.key.toLowerCase();

      const isMacCombination = isMacOS && event.metaKey && event.shiftKey;
      const isWindowsCombination = !isMacOS && event.altKey && !event.shiftKey;

      if ((isMacCombination || isWindowsCombination) && key === 'h') {
        togglePanel('chat');
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleChatKeydown);

    return () => {
      window.removeEventListener('keydown', handleChatKeydown);
    };
  }, [togglePanel, chatAvailable, isMacOS]);

  return null;
});

export default RoomKeyboardShortcuts;
